import { useState, useMemo, useRef, useEffect, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  ClipboardCheck,
  Check,
  X,
  Loader2,
  GripHorizontal,
  Clock,
  Banknote,
  ChevronDown,
  ChevronUp,
  Users,
  BadgeCheck,
  BadgeDollarSign,
  ChevronsDownUp,
  ChevronsUpDown,
  CheckCheck,
  XCircle,
  Building2,
  Download,
  Mail,
  Gift,
  Car,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { http } from '@/network/http';
import useMediaQuery, { MD_BREAKPOINT } from '@/hooks/useMediaQuery';
import { useSettings } from '@/hooks/useSettings';

import { useQueryParams } from '@/hooks/useQueryParams';
import { useMarkPaid, useBulkMarkPaid, useActiveEmploymentRoles } from '../hooks';
import { adminApi } from '../api';
import type { PendingReport } from '../types';
import { localizedName } from '@/utils/localized';
import { useHospitals } from '@/features/hospitals';
import toast from 'react-hot-toast';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import {
  ADMIN_COLORS,
  staggerContainer,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import { GlassCard } from '@/components/glass';
import { StatusBadge } from '../components/StatusBadge';
import { AnimatedValue } from '../components/AnimatedValue';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { ExportWizardModal } from '../components/ExportWizardModal';
import { TimeRangePicker } from '../components/TimeRangePicker';
import { normalizeExportData } from '../utils/export/normalizeExportData';
import { exportCsv } from '../utils/export/csvExporter';
import type { ExportLayout } from '../utils/export/exportTypes';
import type { TimeRange } from '../utils/timeRange';
import {
  createMonthlyRange,
  timeRangeToApiParams,
  isSingleMonth,
} from '../utils/timeRange';

const PAGE_SIZE = 200; // Load all pending for grouping


function GroupStatusBadge({ reports }: { reports: PendingReport[] }) {
  const { t } = useTranslation();
  if (reports.length === 0) return null;
  const nonRejected = reports.filter(r => r.status !== 'REJECTED');
  const hasRejected = reports.some(r => r.status === 'REJECTED');
  const hasPending = nonRejected.some(r => r.status === 'SUBMITTED');
  if (hasPending) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap" style={{ backgroundColor: '#fef9c3', color: '#a16207', boxShadow: '0 1px 4px rgba(161,98,7,0.2)' }}>
        <Clock className="w-3 h-3" /> {t('admin.statusPending')}
      </span>
    );
  }
  if (hasRejected && nonRejected.length === 0) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap" style={{ backgroundColor: '#fee2e2', color: '#dc2626', boxShadow: '0 1px 4px rgba(220,38,38,0.2)' }}>
        <XCircle className="w-3 h-3" /> {t('admin.statusRejected')}
      </span>
    );
  }
  const allPaid = nonRejected.length > 0 && nonRejected.every(r => r.status === 'PAID');
  if (allPaid) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap" style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)', color: '#78350f', boxShadow: '0 1px 5px rgba(120,53,15,0.25)' }}>
        <BadgeDollarSign className="w-3 h-3" /> {t('admin.statusPaid')}
      </span>
    );
  }
  // All approved (or mix of approved+paid, possibly some rejected)
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap" style={{ backgroundColor: '#dcfce7', color: '#15803d', boxShadow: '0 1px 4px rgba(21,128,61,0.2)' }}>
      <Check className="w-3 h-3" /> {t('admin.statusApproved')}
    </span>
  );
}

interface TherapistGroup {
  therapistId: string;
  therapistName: string;
  reports: PendingReport[];
  totalReports: number;
  totalQuantity: number;
  totalAmount: number;
  totalHospitals: number;
}

function employeeName(t: PendingReport['therapist']): string {
  if (t.firstName && t.lastName) return `${t.firstName} ${t.lastName}`;
  if (t.firstName) return t.firstName;
  return '';
}

export function PayrollPage() {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(MD_BREAKPOINT);
  const { hasPermission } = usePermissions();

  // Get currency symbol from system settings
  const { data: settingsData } = useSettings();
  const currencySymbol = settingsData?.currencySymbol ?? '₪';

  const markPaid = useMarkPaid();
  const bulkMarkPaid = useBulkMarkPaid();

  const showCalculations = true;

  const [selectedTherapists, setSelectedTherapists] = useState<Set<string>>(new Set());
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [tooltip, setTooltip] = useState<{ x: number; y: number } | null>(null);
  const [isExportWizardOpen, setIsExportWizardOpen] = useState(false);
  const [wizardExporting, setWizardExporting] = useState(false);

  const currentMonth = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }, []);

  const [params, setParams] = useQueryParams({
    month: { default: currentMonth },
    hospital: { default: '' },
    role: { default: '' },
    name: { default: '' },
    status: { default: '' },
    report: { default: '' },
  });

  const [timeRange, setTimeRange] = useState<TimeRange>(() => createMonthlyRange(params.month));

  const handleTimeRangeChange = (range: TimeRange) => {
    setTimeRange(range);
    // Keep URL month param in sync (for backward compat)
    setParams({ month: range.startMonth });
  };

  const topBarRef = useRef<HTMLDivElement>(null);
  const [, setTopBarVisible] = useState(true);

  useEffect(() => {
    const el = topBarRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setTopBarVisible(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Filter data
  const { data: hospitalsData } = useHospitals();
  const hospitals = hospitalsData?.hospitals ?? [];
  const { data: employmentRoles } = useActiveEmploymentRoles();
  const locale = i18n.language === 'he' ? 'he-IL' : 'en-US';

  // Load all pending reports
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['admin', 'pending-reports', 'infinite', params.status, timeRange.startMonth, timeRange.endMonth],
    queryFn: async ({ pageParam }) => {
      const { data } = await http.get<{ success: boolean; reports: PendingReport[]; allowancesByTherapist: Record<string, Array<{ id: string; name: string; nameEn: string | null; code: string; amount: number; period: string }>>; supervisionBonusesByTherapist: Record<string, { totalAmount: number; breakdown: Array<{ hospitalId: string; hospitalName: string; hospitalNameEn: string | null; hospitalCode: string; reportTypeCode: string; totalUnits: number; bonusPerUnit: number; amount: number }> }>; total: number; statusCounts: { SUBMITTED: number; APPROVED: number; REJECTED: number; PAID: number } }>(
        '/admin/pending-reports',
        { params: { page: pageParam, limit: PAGE_SIZE, ...(params.status && { status: params.status }), ...timeRangeToApiParams(timeRange) } },
      );
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, p) => sum + p.reports.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
  });

  const sentinelRef = useInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  const allReports = useMemo(
    () => data?.pages.flatMap(p => p.reports) ?? [],
    [data],
  );

  const detailReport = useMemo(
    () => allReports.find(r => r.id === params.report) ?? null,
    [allReports, params.report],
  );

  const statusCounts = data?.pages[0]?.statusCounts ?? { SUBMITTED: 0, APPROVED: 0, REJECTED: 0, PAID: 0 };

  // Merge allowances from all pages
  const allowancesByTherapist = useMemo(() => {
    const merged: Record<string, Array<{ id: string; name: string; nameEn: string | null; code: string; amount: number; period: string }>> = {};
    for (const page of data?.pages ?? []) {
      for (const [tid, allowances] of Object.entries(page.allowancesByTherapist || {})) {
        if (!merged[tid]) merged[tid] = allowances;
      }
    }
    return merged;
  }, [data]);

  // Merge supervision bonuses from all pages
  const supervisionBonusesByTherapist = useMemo(() => {
    const merged: Record<string, { totalAmount: number; breakdown: Array<{ hospitalId: string; hospitalName: string; hospitalNameEn: string | null; hospitalCode: string; reportTypeCode: string; totalUnits: number; bonusPerUnit: number; amount: number; contributors?: Array<{ therapistId: string; name: string; units: number }> }> }> = {};
    for (const page of data?.pages ?? []) {
      for (const [tid, svBonus] of Object.entries(page.supervisionBonusesByTherapist || {})) {
        if (!merged[tid]) merged[tid] = svBonus;
      }
    }
    return merged;
  }, [data]);

  // Group by therapist (with filters applied)
  const groups = useMemo(() => {
    const map = new Map<string, TherapistGroup>();
    for (const r of allReports) {
      // Apply hospital filter at the report level
      if (params.hospital && r.hospital?.id !== params.hospital) continue;
      // Apply role filter at the report level (employment role)
      if (params.role && r.therapist.employmentRoleCode !== params.role) continue;

      const tid = r.therapist.id;
      // Apply name filter at therapist level
      if (params.name) {
        const fullName = employeeName(r.therapist).toLowerCase();
        if (!fullName.includes(params.name.toLowerCase())) continue;
      }

      if (!map.has(tid)) {
        map.set(tid, {
          therapistId: tid,
          therapistName: employeeName(r.therapist),
          reports: [],
          totalReports: 0,
          totalQuantity: 0,
          totalAmount: 0,
          totalHospitals: 0,
        });
      }
      const g = map.get(tid)!;
      g.reports.push(r);
      if (r.status !== 'REJECTED') {
        g.totalReports++;
        g.totalQuantity += r.quantity || 1;
        g.totalAmount += r.calculatedAmount ?? 0;
      }
    }
    // Count unique hospitals per therapist
    for (const [, g] of map) {
      g.totalHospitals = new Set(g.reports.filter(r => r.hospital).map(r => r.hospital!.id)).size;
    }
    // Add allowance totals (only for therapists that survived filtering)
    for (const [tid, g] of map) {
      const allowances = allowancesByTherapist[tid] || [];
      for (const a of allowances) {
        g.totalAmount += a.amount;
      }
    }
    // Add supervision bonus totals
    for (const [tid, g] of map) {
      const svBonus = supervisionBonusesByTherapist[tid];
      if (svBonus) {
        g.totalAmount += svBonus.totalAmount;
      }
    }
    // Sort by total amount descending
    return [...map.values()].sort((a, b) => b.totalAmount - a.totalAmount);
  }, [allReports, allowancesByTherapist, supervisionBonusesByTherapist, params.hospital, params.role, params.name]);

  const totalPending = allReports.length;
  const totalEmployees = groups.length;
  const totalAmount = useMemo(
    () => groups.reduce((sum, g) => sum + g.totalAmount, 0),
    [groups],
  );

  const topHospital = useMemo(() => {
    const counts = new Map<string, { name: string; count: number }>();
    for (const g of groups) {
      for (const r of g.reports) {
        if (r.status === 'REJECTED' || !r.hospital) continue;
        const key = r.hospital.id;
        const existing = counts.get(key);
        if (existing) {
          existing.count++;
        } else {
          counts.set(key, { name: localizedName(r.hospital, i18n.language), count: 1 });
        }
      }
    }
    let best: { name: string; count: number } | null = null;
    for (const v of counts.values()) {
      if (!best || v.count > best.count) best = v;
    }
    return best;
  }, [groups, i18n.language]);

  // A therapist is eligible for pay only when ALL non-rejected reports are APPROVED
  const isTherapistEligible = (g: TherapistGroup) => {
    const nonRejected = g.reports.filter(r => r.status !== 'REJECTED');
    return nonRejected.length > 0 && nonRejected.every(r => r.status === 'APPROVED');
  };

  const eligibleTherapistIds = useMemo(
    () => groups.filter(isTherapistEligible).map(g => g.therapistId),
    [groups],
  );

  // Derive selected report objects from selected therapists for bulk operations
  const selectedReportObjects = useMemo(() => {
    const objs: { id: string; reportType: string }[] = [];
    for (const g of groups) {
      if (!selectedTherapists.has(g.therapistId)) continue;
      for (const r of g.reports) {
        objs.push({ id: r.id, reportType: r.reportType });
      }
    }
    return objs;
  }, [groups, selectedTherapists]);

  const toggleTherapist = (therapistId: string) => {
    triggerHapticFeedback();
    const next = new Set(selectedTherapists);
    if (next.has(therapistId)) next.delete(therapistId);
    else next.add(therapistId);
    setSelectedTherapists(next);
  };

  const toggleSelectAll = () => {
    triggerHapticFeedback();
    if (selectedTherapists.size === eligibleTherapistIds.length && eligibleTherapistIds.length > 0) {
      setSelectedTherapists(new Set());
    } else {
      setSelectedTherapists(new Set(eligibleTherapistIds));
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpandAll = () => {
    if (expandedIds.size === groups.length) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(groups.map(g => g.therapistId)));
    }
  };

  const handleMarkPaid = async (report: PendingReport) => {
    triggerHapticFeedback();
    try {
      await markPaid.mutateAsync({ reportType: report.reportType, id: report.id });
      toast.success(t('admin.reportMarkedPaid'));
      if (params.report === report.id) setParams({ report: '' });
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleBulkMarkPaid = async () => {
    if (selectedReportObjects.length === 0) return;
    triggerHapticFeedback([30, 20, 30]);
    const therapistIdsToEmail = [...selectedTherapists];
    try {
      const count = await bulkMarkPaid.mutateAsync(selectedReportObjects);
      setSelectedTherapists(new Set());
      toast.success(`${count} ${t('admin.reportsMarkedPaid')}`);

      // Generate and email payroll PDFs to each therapist (non-blocking)
      (async () => {
        try {
          const { renderPdfBlob } = await import('../components/PayrollPdfDocument');
          await Promise.allSettled(therapistIdsToEmail.map(async (therapistId) => {
            const group = groups.find((g) => g.therapistId === therapistId);
            if (!group) return;
            const payload = normalizeExportData({
              title: t('export.payrollReport'),
              groups: [{ ...group, employeeId: group.therapistId, employeeName: group.therapistName }],
              allowancesByEmployee: allowancesByTherapist,
              supervisionBonusesByEmployee: supervisionBonusesByTherapist,
              selectedIds: new Set<string>(),
              selectionMode: 'employee',
              currencySymbol,
              language: i18n.language,
              timeRange,
              filters: {},
            });
            const blob = await renderPdfBlob(payload, true);
            const buffer = await blob.arrayBuffer();
            const base64 = btoa(
              new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ''),
            );
            await adminApi.emailPayrollPdf(therapistId, base64);
          }));
        } catch (err) {
          console.error('Bulk PDF email error:', err);
        }
      })();
    } catch {
      toast.error(t('common.error'));
    }
  };


  // Email PDF handler
  const [emailingPdfFor, setEmailingPdfFor] = useState<string | null>(null);

  const handleEmailPdf = async (therapistId: string) => {
    setEmailingPdfFor(therapistId);
    try {
      const { renderPdfBlob } = await import('../components/PayrollPdfDocument');
      const group = groups.find((g) => g.therapistId === therapistId);
      if (!group) return;
      const payload = normalizeExportData({
        title: t('export.payrollReport'),
        groups: [{ ...group, employeeId: group.therapistId, employeeName: group.therapistName }],
        allowancesByEmployee: allowancesByTherapist,
        supervisionBonusesByEmployee: supervisionBonusesByTherapist,
        selectedIds: new Set<string>(),
        selectionMode: 'employee',
        currencySymbol,
        language: i18n.language,
        timeRange,
        filters: {},
      });
      const blob = await renderPdfBlob(payload, true);
      const buffer = await blob.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), ''),
      );
      await adminApi.emailPayrollPdf(therapistId, base64);
      toast.success(t('admin.emailPdfSuccess'));
    } catch {
      toast.error(t('common.error'));
    } finally {
      setEmailingPdfFor(null);
    }
  };

  // Export helpers
  const exportFilename = `payroll-${isSingleMonth(timeRange) ? timeRange.startMonth : `${timeRange.startMonth}_${timeRange.endMonth}`}`;

  // Wizard export helpers
  const wizardEmployees = useMemo(() => {
    const seen = new Map<string, { id: string; name: string; role: string }>();
    for (const g of groups) {
      if (!seen.has(g.therapistId)) {
        const firstReport = g.reports[0];
        const role = firstReport?.therapist.employmentRoleName ?? '';
        seen.set(g.therapistId, { id: g.therapistId, name: g.therapistName, role });
      }
    }
    return [...seen.values()];
  }, [groups]);

  const wizardStatuses = useMemo(() => {
    const set = new Set<string>();
    for (const g of groups) {
      for (const r of g.reports) set.add(r.status);
    }
    return [...set];
  }, [groups]);

  const handleWizardExport = async (wizardParams: {
    employeeIds: Set<string>;
    statuses: Set<string>;
    reportTypes: Set<string>;
    aggregated: boolean;
    format: 'csv' | 'xlsx' | 'pdf';
    layout: ExportLayout;
  }) => {
    setWizardExporting(true);
    try {
      const filteredGroups = groups.filter(g => wizardParams.employeeIds.has(g.therapistId));
      const groupsWithFilteredReports = filteredGroups.map(g => ({
        ...g,
        reports: g.reports.filter(r => wizardParams.statuses.has(r.status)),
      })).filter(g => g.reports.length > 0);

      const payload = normalizeExportData({
        title: t('export.payrollReport'),
        groups: groupsWithFilteredReports.map(g => ({ ...g, employeeId: g.therapistId, employeeName: g.therapistName })),
        allowancesByEmployee: allowancesByTherapist,
        supervisionBonusesByEmployee: supervisionBonusesByTherapist,
        selectedIds: new Set<string>(),
        selectionMode: 'employee',
        currencySymbol,
        language: i18n.language,
        timeRange,
        filters: {
          status: params.status || undefined,
          hospitalName: params.hospital ? hospitals.find((h: any) => h.id === params.hospital)?.name : undefined,
          roleName: params.role ? employmentRoles?.find(r => r.code === params.role)?.name : undefined,
        },
      });

      if (wizardParams.format === 'csv') {
        await exportCsv(payload, exportFilename, wizardParams.layout);
      } else if (wizardParams.format === 'xlsx') {
        const { exportXlsx } = await import('../utils/export/xlsxExporter');
        await exportXlsx(payload, exportFilename, wizardParams.layout);
      } else {
        const { exportPdf } = await import('../utils/export/pdfExporter');
        await exportPdf(payload, exportFilename, wizardParams.layout);
      }
    } finally {
      setWizardExporting(false);
    }
  };

  const closeDetailModal = () => setParams({ report: '' });

  // Detail modal content
  const detailContent = detailReport && (
    <>
      <div
        className="sticky top-0 z-10 flex items-center justify-between px-5 py-4"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          borderBottom: `1px solid ${ADMIN_COLORS.border}`,
        }}
      >
        <h3
          className="font-semibold text-sm"
          style={{ color: ADMIN_COLORS.navy, letterSpacing: '0.025em' }}
        >
          {detailReport.reportTypeObj ? localizedName(detailReport.reportTypeObj, i18n.language) : detailReport.reportType}
        </h3>
        <button
          onClick={closeDetailModal}
          className="rounded-lg p-1.5 transition-colors hover:bg-white/20"
        >
          <X className="h-5 w-5" style={{ color: ADMIN_COLORS.textTertiary }} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <div className="space-y-3">
          <div>
            <p className="text-xs mb-0.5" style={{ color: ADMIN_COLORS.textTertiary }}>{t('admin.employee')}</p>
            <p className="text-sm font-semibold" style={{ color: ADMIN_COLORS.navy }}>
              {detailReport.therapist.firstName} {detailReport.therapist.lastName}
            </p>
          </div>
          <div>
            <p className="text-xs mb-0.5" style={{ color: ADMIN_COLORS.textTertiary }}>{t('reports.hospital')}</p>
            <p className="text-sm font-semibold" style={{ color: ADMIN_COLORS.navy }}>
              {localizedName(detailReport.hospital, i18n.language)} ({detailReport.hospital.code})
            </p>
          </div>
          <div>
            <p className="text-xs mb-0.5" style={{ color: ADMIN_COLORS.textTertiary }}>{t('reports.date')}</p>
            <p className="text-sm font-semibold" style={{ color: ADMIN_COLORS.navy }}>{detailReport.date}</p>
          </div>
          {detailReport.treatmentType && (
            <div>
              <p className="text-xs mb-0.5" style={{ color: ADMIN_COLORS.textTertiary }}>{t('reports.treatmentType')}</p>
              <p className="text-sm font-semibold" style={{ color: ADMIN_COLORS.navy }}>
                {t(`treatments.${detailReport.treatmentType}`, detailReport.treatmentType)}
              </p>
            </div>
          )}
          <div>
            <p className="text-xs mb-0.5" style={{ color: ADMIN_COLORS.textTertiary }}>{t('quickReport.quantity')}</p>
            <p className="text-sm font-semibold" style={{ color: ADMIN_COLORS.navy }}>{`×${detailReport.quantity || 1}`}</p>
          </div>
          {detailReport.ratePerUnit > 0 && (
            <div>
              <p className="text-xs mb-0.5" style={{ color: ADMIN_COLORS.textTertiary }}>{t('admin.ratePerUnit')}</p>
              <p className="text-sm font-semibold inline-flex items-center gap-2" style={{ color: ADMIN_COLORS.navy }}>
                {detailReport.ratePerUnit} {currencySymbol}
                {(detailReport.hospitalBonusPerUnit ?? 0) > 0 && (
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
                    style={{ backgroundColor: '#d1fae5', color: '#065f46' }}
                  >
                    +{detailReport.hospitalBonusPerUnit} {t('employmentRoles.hospitalBonuses.title')}
                  </span>
                )}
              </p>
            </div>
          )}
          <div>
            <p className="text-xs mb-0.5" style={{ color: ADMIN_COLORS.textTertiary }}>{t('admin.totalCol')}</p>
            <p className="text-lg font-bold" style={{ color: ADMIN_COLORS.primaryText }}>
              {(detailReport.calculatedAmount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} {currencySymbol}
            </p>
          </div>
          {detailReport.description && (
            <div>
              <p className="text-xs mb-0.5" style={{ color: ADMIN_COLORS.textTertiary }}>{t('reports.notes')}</p>
              <p className="text-sm" style={{ color: ADMIN_COLORS.textSecondary }}>{detailReport.description}</p>
            </div>
          )}
        </div>
      </div>

      <div
        className="sticky bottom-0 flex gap-3 px-5 py-4"
        style={{
          backgroundColor: ADMIN_COLORS.surface,
          borderTop: `1px solid ${ADMIN_COLORS.border}`,
        }}
      >
        {showCalculations && hasPermission('action:manage_payroll') && (() => {
          const group = groups.find(g => g.therapistId === detailReport.therapist.id);
          const eligible = group ? isTherapistEligible(group) : false;
          return eligible ? (
            <button
              onClick={() => handleMarkPaid(detailReport)}
              disabled={markPaid.isPending}
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm transition-all active:translate-y-px disabled:opacity-50"
              style={{
                background: 'linear-gradient(135deg, #a16207, #78350f)',
                color: '#fff',
                boxShadow: '0 2px 8px rgba(120,53,15,0.3)',
              }}
            >
              <BadgeDollarSign className="h-4 w-4" />
              {t('admin.markPaid')}
            </button>
          ) : (
            <p className="flex-1 text-center text-xs font-medium py-3" style={{ color: ADMIN_COLORS.textTertiary }}>
              {t('admin.notFullyApproved')}
            </p>
          );
        })()}
      </div>
    </>
  );

  return (
    <motion.div
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.4 } } }}
      initial="hidden"
      animate="visible"
      className="space-y-4"
    >
      {/* Header */}
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: ADMIN_COLORS.primarySoft }}
        >
          <Banknote className="w-5 h-5" style={{ color: ADMIN_COLORS.primaryText }} />
        </div>
        <div>
          <h1
            className="text-lg sm:text-2xl font-semibold tracking-wide"
            style={{ color: ADMIN_COLORS.navy, letterSpacing: '0.05em' }}
          >
            {t('adminNav.payrollManagement')}
          </h1>
        </div>
      </div>

      {/* Stats — Status counts */}
      <div className="grid grid-cols-4 gap-3">
        <div className="p-3 rounded-3xl glass-surface" style={{ borderInlineStart: '3px solid #a16207', border: '1px solid rgba(255,255,255,0.3)', borderInlineStartWidth: '3px', borderInlineStartColor: '#a16207' }}>
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-3.5 h-3.5 shrink-0" style={{ color: '#a16207' }} />
            <p className="text-[10px] font-bold uppercase" style={{ color: '#a16207' }}>
              {t('admin.statusPending')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={statusCounts.SUBMITTED} />
          </p>
        </div>
        <div className="p-3 rounded-3xl glass-surface" style={{ borderInlineStart: '3px solid #15803d', border: '1px solid rgba(255,255,255,0.3)', borderInlineStartWidth: '3px', borderInlineStartColor: '#15803d' }}>
          <div className="flex items-center gap-2 mb-1">
            <BadgeCheck className="w-3.5 h-3.5 shrink-0" style={{ color: '#15803d' }} />
            <p className="text-[10px] font-bold uppercase" style={{ color: '#15803d' }}>
              {t('admin.statusApproved')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={statusCounts.APPROVED} />
          </p>
        </div>
        <div className="p-3 rounded-3xl glass-surface" style={{ borderInlineStart: '3px solid #78350f', border: '1px solid rgba(255,255,255,0.3)', borderInlineStartWidth: '3px', borderInlineStartColor: '#78350f' }}>
          <div className="flex items-center gap-2 mb-1">
            <BadgeDollarSign className="w-3.5 h-3.5 shrink-0" style={{ color: '#78350f' }} />
            <p className="text-[10px] font-bold uppercase" style={{ color: '#78350f' }}>
              {t('admin.statusPaid')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={statusCounts.PAID} />
          </p>
        </div>
      </div>

      {/* Stats — Filtered totals */}
      <div className="grid grid-cols-4 gap-3">
        <div className="p-3 rounded-3xl border border-white/30 glass-surface">
          <div className="flex items-center gap-2 mb-1">
            <Users className="w-3.5 h-3.5 shrink-0" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <p className="text-[10px] font-bold text-slate-500 uppercase">
              {t('admin.employees')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={totalEmployees} />
          </p>
        </div>
        <div className="p-3 rounded-3xl border border-white/30 glass-surface">
          <div className="flex items-center gap-2 mb-1">
            <ClipboardCheck className="w-3.5 h-3.5 shrink-0" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <p className="text-[10px] font-bold text-slate-500 uppercase">
              {t('admin.reportsCol')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={totalPending} />
          </p>
        </div>
        <div className="p-3 rounded-3xl border border-white/30 glass-surface">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-3.5 h-3.5 shrink-0" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <p className="text-[10px] font-bold text-slate-500 uppercase">
              {t('admin.topHospital')}
            </p>
          </div>
          <p className="text-sm font-bold text-slate-800 truncate">
            {topHospital ? `${topHospital.name} (${topHospital.count})` : '—'}
          </p>
        </div>
        <div className="p-3 rounded-3xl border border-white/30 glass-surface">
          <div className="flex items-center gap-2 mb-1">
            <Banknote className="w-3.5 h-3.5 shrink-0" style={{ color: ADMIN_COLORS.navy + '80' }} />
            <p className="text-[10px] font-bold text-slate-500 uppercase">
              {t('admin.totalAmount')}
            </p>
          </div>
          <p className="text-2xl font-bold text-slate-800">
            <AnimatedValue value={Math.round(totalAmount)} currency />
          </p>
        </div>
      </div>

      {/* Filters */}
      <GlassCard title={t('common.filters')}>
      <div className="space-y-3">
        <TimeRangePicker value={timeRange} onChange={handleTimeRangeChange} compact />
        <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          value={params.name}
          onChange={(e) => setParams({ name: e.target.value })}
          placeholder={t('admin.filterByName')}
          className="rounded-xl border border-white/30 bg-white/60 backdrop-blur-sm px-3 py-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 min-w-[160px]"
        />

        <select
          value={params.status}
          onChange={(e) => setParams({ status: e.target.value })}
          className="rounded-xl border border-white/30 bg-white/60 backdrop-blur-sm px-3 py-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <option value="">{t('common.all')}</option>
          <option value="SUBMITTED">{t('admin.statusPending')}</option>
          <option value="APPROVED">{t('admin.statusApproved')}</option>
          <option value="PAID">{t('admin.statusPaid')}</option>
        </select>

        <select
          value={params.role}
          onChange={(e) => setParams({ role: e.target.value })}
          className="rounded-xl border border-white/30 bg-white/60 backdrop-blur-sm px-3 py-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <option value="">{t('admin.allRoles')}</option>
          {employmentRoles?.map((r) => (
            <option key={r.id} value={r.code}>
              {localizedName(r, i18n.language)}
            </option>
          ))}
        </select>

        <select
          value={params.hospital}
          onChange={(e) => setParams({ hospital: e.target.value })}
          className="rounded-xl border border-white/30 bg-white/60 backdrop-blur-sm px-3 py-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          <option value="">{t('common.all')} {t('hospitals.title')}</option>
          {hospitals.map((h: any) => (
            <option key={h.id} value={h.id}>
              {localizedName(h, i18n.language)}{h.city ? ` (${i18n.language === 'en' && h.cityEn ? h.cityEn : h.city})` : ''}
            </option>
          ))}
        </select>
      </div>
      </div>
      </GlassCard>

      {/* Quick actions */}
      {groups.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={toggleExpandAll}
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-all active:translate-y-px"
            style={{
              backgroundColor: 'rgba(255,255,255,0.5)',
              border: `1px solid ${ADMIN_COLORS.border}`,
              color: ADMIN_COLORS.textSecondary,
            }}
          >
            {expandedIds.size === groups.length ? (
              <><ChevronsDownUp className="w-3.5 h-3.5" /> {t('common.collapseAll')}</>
            ) : (
              <><ChevronsUpDown className="w-3.5 h-3.5" /> {t('common.expandAll')}</>
            )}
          </button>
          <button
            onClick={toggleSelectAll}
            className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-all active:translate-y-px"
            style={{
              backgroundColor: 'rgba(255,255,255,0.5)',
              border: `1px solid ${ADMIN_COLORS.border}`,
              color: ADMIN_COLORS.textSecondary,
            }}
          >
            {selectedTherapists.size === eligibleTherapistIds.length && eligibleTherapistIds.length > 0 ? (
              <><XCircle className="w-3.5 h-3.5" /> {t('common.deselectAll')}</>
            ) : (
              <><CheckCheck className="w-3.5 h-3.5" /> {t('common.selectAll')}</>
            )}
          </button>
          {showCalculations && selectedTherapists.size > 0 && (params.status === 'APPROVED' || params.status === '') && hasPermission('action:manage_payroll') && (
                <button
                  onClick={handleBulkMarkPaid}
                  disabled={bulkMarkPaid.isPending}
                  className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-semibold text-white transition-all active:translate-y-px disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, #a16207, #78350f)',
                    boxShadow: '0 1px 4px rgba(120,53,15,0.3)',
                  }}
                >
                  <BadgeDollarSign className="w-3.5 h-3.5" />
                  {t('admin.markPaid')} ({selectedTherapists.size})
                </button>
          )}
          {hasPermission('action:export_data') && (
            <button
              onClick={() => setIsExportWizardOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-all active:translate-y-px"
              style={{
                backgroundColor: 'var(--color-primary-500)',
                color: '#fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
              }}
            >
              <Download className="w-3.5 h-3.5" />
              {t('export.wizard.title')}
            </button>
          )}
        </div>
      )}

      {/* Loading */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex justify-center py-16"
          >
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: ADMIN_COLORS.textTertiary }} />
          </motion.div>
        ) : allReports.length === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center gap-3 rounded-3xl py-16"
            style={{ border: `2px dashed ${ADMIN_COLORS.borderMedium}` }}
          >
            <ClipboardCheck className="h-10 w-10" style={{ color: ADMIN_COLORS.textTertiary }} />
            <p className="text-sm" style={{ color: ADMIN_COLORS.textSecondary }}>
              {t('admin.noPendingReports')}
            </p>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0 }}
          >
            <div ref={topBarRef} />

            {/* Payroll-style table */}
            <div className="overflow-x-auto rounded-3xl border border-white/30 glass-surface">
              <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '4%' }} />
                </colgroup>
                <thead>
                  <tr className="border-b-[2px] border-white/30" style={{ backgroundColor: 'rgba(255,255,255,0.45)' }}>
                    <th className="ps-3 pe-2 py-2 text-start text-xs sm:text-sm font-semibold">
                      <div className="flex items-center gap-2">
                        <input
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = selectedTherapists.size > 0 && selectedTherapists.size < eligibleTherapistIds.length;
                            }
                          }}
                          type="checkbox"
                          checked={eligibleTherapistIds.length > 0 && selectedTherapists.size === eligibleTherapistIds.length}
                          onChange={toggleSelectAll}
                          disabled={eligibleTherapistIds.length === 0}
                          className="neo-checkbox h-3 w-3 shrink-0"
                        />
                        <span>{t('admin.employee')}</span>
                      </div>
                    </th>
                    <th className="px-1 py-2 text-center text-xs sm:text-sm font-semibold">{t('admin.statusCol')}</th>
                    <th className="px-2 py-2 text-center text-xs sm:text-sm font-semibold">{t('admin.reportsCol')}</th>
                    <th className="px-2 py-2 text-center text-xs sm:text-sm font-semibold">{t('admin.hospitals')}</th>
                    <th className="px-1 py-2 text-center text-xs sm:text-sm font-semibold">{t('quickReport.quantity')}</th>
                    <th className="px-2 py-2 text-center text-xs sm:text-sm font-semibold">{t('admin.subTotal')}</th>
                    <th className="px-2 py-2 text-end text-xs sm:text-sm font-semibold">{t('admin.totalCol')}</th>
                    <th className="ps-1 pe-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group) => {
                    const isExpanded = expandedIds.has(group.therapistId);
                    return (
                      <Fragment key={group.therapistId}>
                        <tr
                          className="border-b border-white/20 bg-white/25 hover:bg-white/35 cursor-pointer"
                          onClick={() => toggleExpand(group.therapistId)}
                        >
                          <td className="ps-3 pe-2 py-1.5 font-medium text-xs text-neutral-800">
                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedTherapists.has(group.therapistId)}
                                onClick={(e) => {
                                  if (!isTherapistEligible(group)) {
                                    e.preventDefault();
                                    const rect = (e.target as HTMLElement).getBoundingClientRect();
                                    setTooltip({ x: rect.left + rect.width / 2, y: rect.top });
                                    setTimeout(() => setTooltip(null), 2000);
                                  }
                                }}
                                onChange={() => {
                                  if (isTherapistEligible(group)) toggleTherapist(group.therapistId);
                                }}
                                className={`neo-checkbox h-3 w-3 shrink-0${!isTherapistEligible(group) ? ' opacity-30 cursor-not-allowed' : ''}`}
                              />
                              <span className="truncate" onClick={(e) => { e.stopPropagation(); toggleExpand(group.therapistId); }}>{group.therapistName}</span>
                            </div>
                          </td>
                          <td className="px-1 py-1.5 text-center">
                            <GroupStatusBadge reports={group.reports} />
                          </td>
                          <td className="px-2 py-1.5 text-center text-xs text-neutral-600">
                            {group.totalReports}
                          </td>
                          <td className="px-2 py-1.5 text-center text-xs text-neutral-600">
                            {group.totalHospitals}
                          </td>
                          <td className="px-1 py-1.5 text-center text-xs text-neutral-600">
                            {group.totalQuantity}
                          </td>
                          <td className="px-2 py-1.5" />
                          <td className="px-2 py-1.5 text-end font-medium text-xs text-neutral-800">
                            {showCalculations ? `${group.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currencySymbol}` : '—'}
                          </td>
                          <td className="ps-1 pe-3 py-1.5">
                            <div className="flex items-center gap-1">
                              {hasPermission('action:manage_payroll') && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleEmailPdf(group.therapistId); }}
                                  disabled={emailingPdfFor === group.therapistId}
                                  className="rounded-md p-1 transition-colors hover:bg-white/30 disabled:opacity-50"
                                  title={t('admin.emailPdf')}
                                >
                                  {emailingPdfFor === group.therapistId ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" />
                                  ) : (
                                    <Mail className="h-3.5 w-3.5 text-slate-500" />
                                  )}
                                </button>
                              )}
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4 text-slate-500" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-slate-500" />
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (() => {
                          const allowances = allowancesByTherapist[group.therapistId] || [];
                          const svBonus = supervisionBonusesByTherapist[group.therapistId];
                          const svBreakdown = svBonus?.breakdown || [];
                          const svRowCount = svBreakdown.reduce((sum: number, sv: any) => sum + Math.max(sv.contributors?.length || 0, 1), 0);
                          const totalChildren = group.reports.length + allowances.length + svRowCount;
                          let childIdx = 0;
                          const lineColor = 'color-mix(in srgb, var(--color-primary-500) 30%, transparent)';
                          return (
                            <>
                              {group.reports.map((r) => {
                                const idx = childIdx++;
                                const isLast = idx === totalChildren - 1;
                                const typeName = r.reportTypeObj ? localizedName(r.reportTypeObj, i18n.language) : r.reportTypeCode || r.reportType;
                                return (
                                  <tr
                                    key={r.id}
                                    className="bg-white/8 text-xs border-b border-white/10 hover:bg-white/15 cursor-pointer"
                                    onClick={() => setParams({ report: r.id })}
                                  >
                                    <td className="ps-3 pe-2 py-1.5 text-neutral-500" style={{ position: 'relative' }}>
                                      {/* Tree connector */}
                                      <div style={{
                                        position: 'absolute',
                                        insetInlineStart: '0.75rem',
                                        top: 0,
                                        bottom: isLast ? '50%' : 0,
                                        width: '1px',
                                        backgroundColor: lineColor,
                                      }} />
                                      <div style={{
                                        position: 'absolute',
                                        insetInlineStart: '0.75rem',
                                        top: '50%',
                                        width: '0.5rem',
                                        height: '1px',
                                        backgroundColor: lineColor,
                                      }} />
                                      <div style={{
                                        position: 'absolute',
                                        insetInlineStart: 'calc(0.75rem + 0.5rem - 2px)',
                                        top: 'calc(50% - 2px)',
                                        width: '4px',
                                        height: '4px',
                                        borderRadius: '50%',
                                        backgroundColor: lineColor,
                                      }} />
                                      <span className="truncate" style={{ marginInlineStart: '1.25rem' }}>{r.date}</span>
                                    </td>
                                    <td className="px-1 py-1.5 text-center">
                                      <StatusBadge status={r.status} />
                                    </td>
                                    <td className="px-2 py-1.5 text-center text-neutral-500 truncate">
                                      {typeName}
                                    </td>
                                    <td className="px-2 py-1.5 text-center text-neutral-500 truncate">
                                      {r.hospital ? localizedName(r.hospital, i18n.language) : ''}
                                    </td>
                                    <td className="px-1 py-1.5 text-center text-neutral-500">
                                      {r.quantity || 1}
                                    </td>
                                    <td className={`px-2 py-1.5 text-center text-neutral-500${r.status === 'REJECTED' ? ' line-through opacity-50' : ''}`}>
                                      {r.ratePerUnit > 0 ? (
                                        (r.hospitalBonusPerUnit ?? 0) > 0 ? (
                                          <>{(r.quantity || 1) > 1 ? `${r.quantity}×(` : ''}{r.ratePerUnit}+{r.hospitalBonusPerUnit}{(r.quantity || 1) > 1 ? ')' : ''}</>
                                        ) : (
                                          <>{(r.quantity || 1) > 1 ? `${r.quantity}×` : ''}{r.ratePerUnit}</>
                                        )
                                      ) : '-'}
                                    </td>
                                    <td className={`px-2 py-1.5 text-end font-medium text-neutral-700${r.status === 'REJECTED' ? ' line-through opacity-50' : ''}`}>
                                      {showCalculations && r.calculatedAmount != null ? `${r.calculatedAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currencySymbol}` : '—'}
                                    </td>
                                    <td className="ps-1 pe-3 py-1.5" />
                                  </tr>
                                );
                              })}
                              {svBreakdown.flatMap((sv: any) => {
                                const contributors = sv.contributors || [];
                                const rows = contributors.length > 0 ? contributors : [null];
                                return rows.map((contrib: any, cIdx: number) => {
                                  const idx = childIdx++;
                                  const isLast = idx === totalChildren - 1;
                                  const contribAmount = contrib ? contrib.units * sv.bonusPerUnit : sv.amount;
                                  const contribUnits = contrib ? contrib.units : sv.totalUnits;
                                  return (
                                    <tr key={`sv-${sv.hospitalId}-${sv.reportTypeCode}-${contrib?.therapistId || cIdx}`} className="bg-amber-50/30 text-xs border-b border-white/15">
                                      <td className="ps-3 pe-2 py-1.5 text-amber-700 font-medium" style={{ position: 'relative' }}>
                                        {/* Tree connector */}
                                        <div style={{
                                          position: 'absolute',
                                          insetInlineStart: '0.75rem',
                                          top: 0,
                                          bottom: isLast ? '50%' : 0,
                                          width: '1px',
                                          backgroundColor: lineColor,
                                        }} />
                                        <div style={{
                                          position: 'absolute',
                                          insetInlineStart: '0.75rem',
                                          top: '50%',
                                          width: '0.5rem',
                                          height: '1px',
                                          backgroundColor: lineColor,
                                        }} />
                                        <div style={{
                                          position: 'absolute',
                                          insetInlineStart: 'calc(0.75rem + 0.5rem - 2px)',
                                          top: 'calc(50% - 2px)',
                                          width: '4px',
                                          height: '4px',
                                          borderRadius: '50%',
                                          backgroundColor: lineColor,
                                        }} />
                                        <span className="truncate" style={{ marginInlineStart: '1.25rem', display: 'block' }}>
                                          {contrib ? <span className="italic text-amber-600/80 inline-flex items-center gap-1"><Gift className="h-3 w-3 shrink-0" />{contrib.name}</span> : t('admin.supervisionBonus')}
                                        </span>
                                      </td>
                                      <td className="px-1 py-1.5" />
                                      <td className="px-2 py-1.5 text-center text-amber-700 truncate">
                                        {t('admin.supervisionBonus')}
                                      </td>
                                      <td className="px-2 py-1.5 text-center text-amber-700 font-medium truncate">
                                        {i18n.language === 'he' ? sv.hospitalName : (sv.hospitalNameEn || sv.hospitalName)}
                                      </td>
                                      <td className="px-1 py-1.5 text-center text-amber-700">{contribUnits}</td>
                                      <td className="px-2 py-1.5 text-center text-amber-700">
                                        {contribUnits}×{sv.bonusPerUnit}
                                      </td>
                                      <td className="px-2 py-1.5 text-end font-medium text-amber-800">
                                        {showCalculations ? `${contribAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currencySymbol}` : '—'}
                                      </td>
                                      <td className="ps-1 pe-3 py-1.5" />
                                    </tr>
                                  );
                                });
                              })}
                              {allowances.map((a) => {
                                const idx = childIdx++;
                                const isLast = idx === totalChildren - 1;
                                return (
                                  <tr key={`allowance-${a.id}`} className="bg-primary-50/30 text-xs border-b border-white/15">
                                    <td className="ps-3 pe-2 py-1.5 text-primary-600 font-medium" style={{ position: 'relative' }}>
                                      {/* Tree connector */}
                                      <div style={{
                                        position: 'absolute',
                                        insetInlineStart: '0.75rem',
                                        top: 0,
                                        bottom: isLast ? '50%' : 0,
                                        width: '1px',
                                        backgroundColor: lineColor,
                                      }} />
                                      <div style={{
                                        position: 'absolute',
                                        insetInlineStart: '0.75rem',
                                        top: '50%',
                                        width: '0.5rem',
                                        height: '1px',
                                        backgroundColor: lineColor,
                                      }} />
                                      <div style={{
                                        position: 'absolute',
                                        insetInlineStart: 'calc(0.75rem + 0.5rem - 2px)',
                                        top: 'calc(50% - 2px)',
                                        width: '4px',
                                        height: '4px',
                                        borderRadius: '50%',
                                        backgroundColor: lineColor,
                                      }} />
                                      <span className="truncate inline-flex items-center gap-1" style={{ marginInlineStart: '1.25rem' }}>
                                        {a.code === 'TRAVEL' && <Car className="h-3 w-3 shrink-0" />}
                                        {t('admin.allowance')}
                                      </span>
                                    </td>
                                    <td className="px-1 py-1.5" />
                                    <td className="px-2 py-1.5" />
                                    <td className="px-2 py-1.5 text-center text-primary-600 font-medium truncate">
                                      {i18n.language === 'he' ? a.name : (a.nameEn || a.name)}
                                    </td>
                                    <td className="px-1 py-1.5 text-center text-primary-600">1</td>
                                    <td className="px-2 py-1.5 text-center text-primary-600">
                                      1×{a.amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                    </td>
                                    <td className="px-2 py-1.5 text-end font-medium text-primary-700">
                                      {showCalculations ? `${a.amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currencySymbol}` : '—'}
                                    </td>
                                    <td className="ps-1 pe-3 py-1.5" />
                                  </tr>
                                );
                              })}
                            </>
                          );
                        })()}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-[2px] border-white/30 text-xs sm:text-sm font-semibold" style={{ backgroundColor: 'rgba(255,255,255,0.45)' }}>
                    <td className="pe-2 py-2" style={{ paddingInlineStart: 'calc(0.75rem + 12px + 8px)' }}>{t('admin.total')} ({totalEmployees})</td>
                    <td className="px-1 py-2" />
                    <td className="px-2 py-2 text-center">{totalPending}</td>
                    <td className="px-2 py-2 text-center">{groups.reduce((sum, g) => sum + g.totalHospitals, 0)}</td>
                    <td className="px-1 py-2 text-center">{groups.reduce((sum, g) => sum + g.totalQuantity, 0)}</td>
                    <td className="px-2 py-2" />
                    <td className="px-2 py-2 text-end font-bold">
                      {showCalculations ? `${Math.round(totalAmount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currencySymbol}` : '—'}
                    </td>
                    <td className="ps-1 pe-3 py-2" />
                  </tr>
                </tfoot>
              </table>
            </div>


            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} className="h-4" />
            {isFetchingNextPage && (
              <div className="flex justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin" style={{ color: ADMIN_COLORS.textTertiary }} />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

{/* Removed FAB — approve bar is inline above/below table */}

      {/* Detail Modal -- Desktop */}
      <AnimatePresence>
        {detailReport && !isMobile && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]"
              style={{ left: '8rem' }}
              onClick={closeDetailModal}
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className="fixed z-[101] flex items-center justify-center p-4"
              style={{ left: '8rem', right: 0, top: 0, bottom: 0 }}
              onClick={closeDetailModal}
            >
              <div
                className="w-full max-w-lg max-h-[80dvh] overflow-hidden rounded-3xl flex flex-col"
                style={{
                  backgroundColor: ADMIN_COLORS.surface,
                  border: `1px solid ${ADMIN_COLORS.border}`,
                  boxShadow: ADMIN_COLORS.shadowLg,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {detailContent}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Detail Modal -- Mobile: bottom drawer */}
      <AnimatePresence>
        {detailReport && isMobile && createPortal(
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            drag="y"
            dragConstraints={{ top: 0 }}
            dragElastic={0.2}
            onDragEnd={(_, info) => { if (info.offset.y > 100) closeDetailModal(); }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-[101] flex flex-col overflow-hidden pt-safe pb-safe"
            style={{ height: '100dvh', backgroundColor: ADMIN_COLORS.surface }}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex justify-center py-3">
              <GripHorizontal className="h-5 w-5" style={{ color: ADMIN_COLORS.textTertiary }} />
            </div>
            {detailContent}
          </motion.div>,
          document.body,
        )}
      </AnimatePresence>

      {/* Ineligible tooltip */}
      {tooltip && createPortal(
        <div
          className="fixed z-[9999] pointer-events-none animate-in fade-in slide-in-from-bottom-1 duration-150"
          style={{
            left: tooltip.x,
            top: tooltip.y - 8,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div
            className="rounded-lg px-3 py-1.5 text-xs font-medium text-white whitespace-nowrap shadow-lg"
            style={{ backgroundColor: '#334155' }}
          >
            {t('admin.notFullyApproved')}
          </div>
        </div>,
        document.body,
      )}
      <ExportWizardModal
        isOpen={isExportWizardOpen}
        onClose={() => setIsExportWizardOpen(false)}
        employees={wizardEmployees}
        statuses={wizardStatuses}
        onExport={handleWizardExport}
        isPending={wizardExporting}
      />
    </motion.div>
  );
}
